import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';

const PRESENCE_COLORS = { online: '#10b981', away: '#f59e0b', offline: '#6b7280' };

export const meta = {
  id: 'agent_presence',
  category: 'team',
  titleKey: 'dashboard.widgets.agentPresence',
  descriptionKey: 'dashboard.widgets.agentPresenceDesc',
  defaultSize: 'xl',
  minRole: 'admin',
  icon: Users,
};

export default function AgentPresenceWidget() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const locale = user?.language === 'en' ? enUS : es;

  const { data: presenceData } = useQuery({
    queryKey: ['agent-presence'],
    queryFn: () => api.get('/presence/agents').then(r => r.data),
    refetchInterval: 30000,
  });

  const agents = presenceData || [];
  if (agents.length === 0) {
    return (
      <div className="card" style={{ height: '100%' }}>
        <div className="card-header">
          <div className="card-title">{t('dashboard.agentPresence', 'Agent Presence')}</div>
        </div>
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>—</div>
      </div>
    );
  }

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header">
        <div className="card-title">{t('dashboard.agentPresence', 'Agent Presence')}</div>
        <span style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{t('dashboard.updatesEvery30s', 'Updates every 30s')}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '160px' : '220px'}, 1fr))`, gap: 10 }}>
        {agents.map(agent => (
          <div
            key={agent.id}
            onClick={() => navigate(`/tickets?assigned_to=${agent.id}&status=__active__`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 12px',
              background: 'var(--c-surface-2)',
              borderRadius: 8,
              border: `1px solid ${(PRESENCE_COLORS[agent.presence] || '#6b7280') + '44'}`,
              cursor: 'pointer',
            }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div className="user-avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
                {agent.avatar_url
                  ? <img src={agent.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  : agent.name?.[0] || '?'}
              </div>
              <div style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 10, height: 10, borderRadius: '50%',
                background: PRESENCE_COLORS[agent.presence] || '#6b7280',
                border: '2px solid var(--c-surface-2)',
              }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {agent.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                <span style={{ fontSize: 11, color: PRESENCE_COLORS[agent.presence], textTransform: 'capitalize', fontWeight: 500 }}>
                  {agent.presence}
                </span>
                <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>·</span>
                <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                  {agent.open_tickets || 0} {t('dashboard.open', 'open')}
                </span>
                {agent.presence !== 'online' && agent.last_seen_at && (
                  <>
                    <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>·</span>
                    <span style={{ fontSize: 10, color: 'var(--c-text-subtle)' }}>
                      {formatDistanceToNow(new Date(agent.last_seen_at), { addSuffix: true, locale })}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
